import { Link } from "react-router-dom";
import {
  AppWindow,
  LifeBuoy,
  MessagesSquare,
  Palette,
  Search,
  Smartphone,
  Target,
} from "lucide-react";
import { Seo } from "@/components/Seo";
import { WHY_ITEMS } from "@/data/content";

const WHY_ICONS = { Target, Palette, Smartphone, AppWindow, Search, LifeBuoy, MessagesSquare };

export default function About() {
  return (
    <div data-testid="about-page">
      <Seo
        title="À propos — Koranex"
        description="Koranex accompagne entreprises, PME et entrepreneurs dans leur présence numérique : sites web, applications mobiles, SEO et maintenance, avec un suivi de A à Z."
      />
      <header className="py-16 md:py-24 px-6 max-w-6xl mx-auto">
        <span className="text-xs font-mono-x uppercase text-[#38bdf8] tracking-widest kx-up inline-block">
          À propos
        </span>
        <h1 className="font-display text-4xl sm:text-6xl md:text-7xl text-white leading-none mt-3 mb-6 kx-up delay-100">
          Un partenaire numérique, pas un simple prestataire
        </h1>
        <p className="text-base sm:text-lg text-slate-300 max-w-3xl leading-relaxed kx-up delay-200">
          Koranex accompagne les entreprises, PME, entrepreneurs, commerçants et organisations qui veulent développer
          leur présence numérique. Notre mission : concevoir des sites web, des applications mobiles et des stratégies
          SEO qui servent réellement votre activité.
        </p>
      </header>

      <section className="py-12 md:py-16 bg-[#050b18] border-t border-slate-800 px-6">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <h2 className="font-display text-3xl sm:text-4xl text-white mb-6">Notre façon de travailler</h2>
            <div className="space-y-5 text-sm text-slate-300 leading-relaxed">
              <p>
                Nous travaillons entièrement en ligne, ce qui nous permet d'accompagner des clients partout, avec la
                même exigence de qualité. Chaque projet commence par un vrai échange : comprendre votre activité, vos
                clients et vos objectifs avant de parler technique.
              </p>
              <p>
                Nos clients nous choisissent pour trois raisons : un accompagnement de A à Z, y compris après la mise
                en ligne ; un design soigné, moderne et sur mesure ; et une communication claire avec des réponses
                rapides à chaque étape.
              </p>
              <p>
                Pas de jargon inutile, pas de promesses impossibles : des solutions concrètes, un suivi transparent et
                des livrables que vous comprenez et maîtrisez.
              </p>
            </div>
          </div>
          <div className="rounded-2xl bg-[#0f172a] border border-slate-800 p-8">
            <h2 className="font-display text-2xl sm:text-3xl text-white mb-6">Nos engagements</h2>
            <ul className="space-y-4">
              <li className="flex gap-3 text-sm text-slate-300">
                <MessagesSquare className="w-5 h-5 text-[#38bdf8] shrink-0" />
                Une communication claire et un suivi de projet transparent.
              </li>
              <li className="flex gap-3 text-sm text-slate-300">
                <Palette className="w-5 h-5 text-[#38bdf8] shrink-0" />
                Un design professionnel et sur mesure, jamais un modèle générique.
              </li>
              <li className="flex gap-3 text-sm text-slate-300">
                <LifeBuoy className="w-5 h-5 text-[#38bdf8] shrink-0" />
                Un accompagnement après la livraison : maintenance, SEO, évolutions.
              </li>
              <li className="flex gap-3 text-sm text-slate-300">
                <Target className="w-5 h-5 text-[#38bdf8] shrink-0" />
                Des devis gratuits, détaillés et sans engagement.
              </li>
            </ul>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 px-6 max-w-6xl mx-auto">
        <h2 className="font-display text-3xl sm:text-5xl text-white mb-12">Ce qui nous distingue</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {WHY_ITEMS.map((item) => {
            const Icon = WHY_ICONS[item.icon];
            return (
              <div
                key={item.title}
                className="p-6 rounded-2xl bg-[#0f172a] border border-slate-800 hover:border-[#1d4ed8] transition-colors"
              >
                <div className="w-10 h-10 rounded-lg bg-[#1d4ed8]/20 border border-[#38bdf8]/30 flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-[#38bdf8]" />
                </div>
                <h3 className="font-bold text-white text-base mb-1">{item.title}</h3>
                <p className="text-xs text-slate-400 leading-relaxed">{item.text}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="pb-16 md:pb-24 px-6 max-w-6xl mx-auto text-center">
        <h2 className="font-display text-3xl sm:text-5xl text-white mb-4">Envie de travailler ensemble ?</h2>
        <p className="text-sm text-slate-400 mb-8 max-w-xl mx-auto">
          Présentez-nous votre projet : nous vous répondons avec un devis gratuit et sans engagement.
        </p>
        <Link
          to="/contact"
          data-testid="about-quote-button"
          className="inline-flex px-8 py-4 rounded-xl bg-[#1d4ed8] hover:bg-blue-600 text-white font-mono-x text-xs uppercase tracking-wider font-bold transition-colors shadow-xl shadow-blue-950 kx-glow"
        >
          Demander un devis gratuit
        </Link>
      </section>
    </div>
  );
}
